/**
 * materialDetailsService.js
 *
 * Responsible for building the `materialDetails` array that goes into the
 * vaccination record payload.  Each of the three vaccination scenarios
 * (rejection, current, previous) has its own function so the logic is
 * easy to read, test, and change in isolation.
 *
 * ENCRYPTION CONTRACT
 * -------------------
 * All three functions receive an `encryptText` async helper rather than
 * calling uwinAPI directly.  The helper is created once inside
 * createVaccinationRecord and passed down here, which keeps every
 * function pure-async (no API imports at this layer) and makes unit
 * testing trivial – just pass a mock encryptText.
 *
 * DOSE NUMBER CONTRACT
 * --------------------
 * getDoseNumber(doseName, displayDoseNames) is imported from the
 * VaccinationModule/vaccinationUtils barrel – the single source of truth
 * used throughout the vaccination system.  This guarantees the dose number
 * calculation is identical to every other consumer.
 */

import { getDoseNumber } from '../VaccinationModule/vaccinationUtils';

// ─────────────────────────────────────────────────────────────────────────────
// REJECTION SCENARIO
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build materialDetails for a vaccination REJECTION record.
 *
 * When a beneficiary refuses or cannot receive a vaccine the API still
 * expects a material list so the session record is complete.  Dose number
 * is derived from the user's selectedDose on the current vaccination form
 * (same getDoseNumber logic as buildCurrentMaterialDetails).
 *
 * @param {Object}   rejectionData              - Rejection payload from the form
 * @param {Array}    rejectionData.selectedVaccines - Vaccines that were offered/rejected
 * @param {Function} encryptText                - async (plaintext: string) => encryptedString
 * @returns {Promise<Array>} materialDetails array ready for the API payload
 */
export const buildRejectionMaterialDetails = async (rejectionData, encryptText) => {
  const materialDetails = [];

  // Nothing to encrypt when no vaccines were selected – return empty array
  // so payload.materialDetails is still present but empty (matches original behaviour)
  if (!rejectionData?.selectedVaccines || rejectionData.selectedVaccines.length === 0) {
    return materialDetails;
  }

  for (const vaccine of rejectionData.selectedVaccines) {
    const doseNumber = getDoseNumber(
      vaccine.selectedDose,
      vaccine.displayDoseNames || []
    );
    const encryptedDoseNumber = await encryptText(doseNumber.toString());
    const encryptedMaterialId = await encryptText(vaccine.id.toString());

    const doseObj = {
      material_id: encryptedMaterialId,
      material_name: vaccine.name,
      material_dose: encryptedDoseNumber,
    };

    const isPrecaution =
      vaccine.availableDoses?.find((d) => d.doseName === vaccine.selectedDose)
        ?.isPrecaution || false;
    if (isPrecaution) {
      doseObj.is_precaution = true;
    }

    materialDetails.push(doseObj);
  }

  return materialDetails;
};

// ─────────────────────────────────────────────────────────────────────────────
// CURRENT VACCINATION SCENARIO
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build materialDetails for a CURRENT vaccination record.
 *
 * "Current" means the vaccination is being recorded for today's session.
 * The beneficiary has a radio-button selected dose per vaccine.
 * We derive the numeric dose number from getDoseNumber and encrypt it.
 *
 * @param {Object}   formData              - Formik values from the vaccination form
 * @param {Array}    formData.current_vaccines - Array of vaccine objects with selection state
 * @param {Function} encryptText           - async (plaintext: string) => encryptedString
 * @returns {Promise<Array>} materialDetails array ready for the API payload
 * @throws  {Error}  When no vaccines with a dose are selected
 */
export const buildCurrentMaterialDetails = async (formData, encryptText) => {
  const activeVaccines = formData.current_vaccines || [];

  // Collect only vaccines the user has selected AND assigned a dose to
  const selectedCurrentVaccines = activeVaccines
    .filter(vaccine => vaccine.selected && vaccine.selectedDose)
    .map(vaccine => ({
      material_id: vaccine.id,
      material_name: vaccine.name,
      material_dose_name: vaccine.selectedDose,
      // displayDoseNames is the ordered array used by getDoseNumber to map
      // a human-readable dose name back to its 1-based index
      displayDoseNames: vaccine.displayDoseNames || [],
      isPrecaution: vaccine.availableDoses?.find(d => d.doseName === vaccine.selectedDose)?.isPrecaution || false,
    }));

  // Guard: surface a clear error rather than silently posting an empty list
  if (selectedCurrentVaccines.length === 0) {
    throw new Error('Please select at least one vaccine with a dose');
  }

  const encryptedMaterialDetails = [];

  for (const vaccine of selectedCurrentVaccines) {
    const doseNumber = getDoseNumber(vaccine.material_dose_name, vaccine.displayDoseNames);
    const encryptedDoseNumber = await encryptText(doseNumber.toString());
    const encryptedMaterialId = await encryptText(vaccine.material_id.toString());

    const doseObj = {
      material_id: encryptedMaterialId,
      material_name: vaccine.material_name,
      material_dose: encryptedDoseNumber,
    };
    
    if (vaccine.isPrecaution) {
      doseObj.is_precaution = true;
    }
    
    encryptedMaterialDetails.push(doseObj);
  }

  return encryptedMaterialDetails;
};

// ─────────────────────────────────────────────────────────────────────────────
// PREVIOUS VACCINATION SCENARIO
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build materialDetails for a PREVIOUS (past-dated) vaccination record.
 *
 * "Previous" means the vaccination happened before today's session.
 * Each vaccine can have multiple individually-selectable doses, each with
 * its own administered date.  We flatten the structure into a single list
 * of dose entries, encrypt each dose number, and keep the date alongside it.
 *
 * @param {Object}   formData               - Formik values from the vaccination form
 * @param {Array}    formData.previous_vaccines - Array of vaccine objects with dose state
 * @param {Function} encryptText            - async (plaintext: string) => encryptedString
 * @returns {Promise<Array>} materialDetails array ready for the API payload
 */
export const buildPreviousMaterialDetails = async (formData, encryptText) => {
  const previousVaccines = formData.previous_vaccines || [];

  // Step 1: Flatten vaccines -> doses into a single list of selected dose entries.
  // A "previous" vaccine is selected at the vaccine level AND the dose level
  // (checkbox per dose), so we need both flags.
  const selectedPreviousDoses = [];

  previousVaccines.forEach(vaccine => {
    if (vaccine.selected && vaccine.availableDoses) {
      vaccine.availableDoses.forEach((dose) => {
        if (dose.selected) {
          const doseNumber = getDoseNumber(dose.doseName, vaccine.displayDoseNames || []);

          selectedPreviousDoses.push({
            material_id: vaccine.id,
            material_name: vaccine.name,
            // Numeric dose number – will be encrypted in the next step
            material_dose: doseNumber,
            // null is valid here when date is unknown; API handles it
            prev_vaccination_date: dose.administeredDate || null,
            isPrecaution: dose.isPrecaution || false,
          });
        }
      });
    }
  });

  // Step 2: Encrypt each dose number.
  // We keep the for...of + await pattern so encryptions run sequentially,
  // which matches the original behaviour and avoids race conditions on the
  // encryption endpoint.
  const encryptedMaterialDetails = [];

  for (const dose of selectedPreviousDoses) {
    const encryptedDoseNumber = await encryptText(dose.material_dose.toString());
    const encryptedMaterialId = await encryptText(dose.material_id.toString());

    const doseObj = {
      material_id: encryptedMaterialId,
      material_name: dose.material_name,
      material_dose: encryptedDoseNumber,
    };

    // Only include prev_vaccination_date when the user actually entered a date.
    // Sending null is not accepted by the backend — the key must be absent entirely.
    if (dose.prev_vaccination_date) {
      doseObj.prev_vaccination_date = dose.prev_vaccination_date;
    }

    if (dose.isPrecaution) {
      doseObj.is_precaution = true;
    }
    
    encryptedMaterialDetails.push(doseObj);
  }

  return encryptedMaterialDetails;
};
